"use client";

import Link from "next/link";
import { useState, type FormEvent } from "react";
import { requestAccountDeletion, type DeletionRequestResult } from "./actions";

export function DeletionRequestForm() {
  const [confirmation, setConfirmation] = useState("");
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<DeletionRequestResult | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending || confirmation !== "DELETE") return;
    setPending(true);
    setResult(null);
    try {
      setResult(await requestAccountDeletion(confirmation));
    } catch {
      setResult({ error: "Your request could not be confirmed. Please try again." });
    } finally {
      setPending(false);
    }
  }

  if (result && "success" in result) {
    return <p role="status" className="rounded-lg border border-border-light bg-white p-5 leading-7">Your deletion request has been received. Your account has not been deleted yet. We will complete the request within 30 days and email you when it is complete.</p>;
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-border-light bg-white p-5 sm:p-6">
      <h2 className="font-serif text-2xl text-navy">Request permanent account deletion</h2>
      <p id="deletion-request-description" className="mt-3 text-sm leading-6 text-text-muted">This applies to your signed-in account, including accounts with employer access. We will remove your account and associated personal data within 30 days and email you when complete. Once deleted, your account cannot be restored.</p>
      <label htmlFor="deletion-confirmation" className="mt-5 block text-sm font-semibold text-navy">Type DELETE to confirm</label>
      <input id="deletion-confirmation" aria-describedby="deletion-request-description" autoComplete="off" spellCheck={false} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} disabled={pending} className="mt-2 min-h-11 w-full rounded-lg border border-text-muted px-3 py-2 text-navy focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy" />
      <button type="submit" disabled={pending || confirmation !== "DELETE"} className="mt-4 inline-flex min-h-11 items-center justify-center rounded-lg bg-navy px-5 py-3 text-sm font-semibold text-white disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy">{pending ? "Sending request..." : "Request account deletion"}</button>
      {result && "error" in result ? <div role="alert" className="mt-4 text-sm leading-6 text-red-700"><p>{result.error}</p>{result.signInRequired ? <Link href="/login?redirect=/delete-account" className="inline-flex min-h-11 items-center font-semibold underline">Sign in to continue</Link> : null}</div> : null}
    </form>
  );
}
